import { type WithRouterProps } from 'next/dist/client/with-router'
import { withRouter } from 'next/router'
import React from 'react'

import { feMetrics } from '@lib/tracking/metrics/femetrics'
import { GeneralClientMetrics, ClientMetricsActions } from '@lib/tracking/metrics/metrics'

import { logError, LogErrorMetrics } from '../../lib/tracking/felog'
import { transformToObject } from '../../lib/utils/error'

const wontFixReactCrashMessage = "Won't Fix React crash" // 'wontFix' are known errors we intentionally are not fixing. There are no alerts associated with them.
const expectedReactCrashMessage = 'Expected React crash' // 'expected' are known errors we intend to fix that are prioritized. There are alerts associated with them at a higher threshold.
const unexpectedReactCrashMessage = 'Unexpected React crash' // 'unexpected' are errors we should look into and categorize as 'expected' or 'wontFix'. There are alerts associated with them at a low threshold.

const wontFixErrorMessages: Set<string> = new Set([])
const exactKnownErrorMessages: Set<string> = new Set([])
const prefixKnownErrorMessages: Array<string> = []

interface ErrorBoundaryProps {
  children: React.ReactNode
  fallback?: React.ReactNode
}

interface ErrorBoundaryState {
  hasError: boolean
}

class ErrorBoundary extends React.Component<
  ErrorBoundaryProps & WithRouterProps,
  ErrorBoundaryState
> {
  isRouting = false

  constructor(props: ErrorBoundaryProps & WithRouterProps) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError() {
    return { hasError: true }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    if (this.isRouting) {
      return
    }
    try {
      const message = wontFixErrorMessages.has(error.message)
        ? wontFixReactCrashMessage
        : exactKnownErrorMessages.has(error.message) ||
            prefixKnownErrorMessages.some(knownError => error.message.startsWith(knownError))
          ? expectedReactCrashMessage
          : unexpectedReactCrashMessage
      logError(`Boundary Error: ${message}`, LogErrorMetrics.BoundaryError, {
        componentStack: errorInfo.componentStack || '',
        error: transformToObject(error),
      })
      feMetrics
        .getMetric(GeneralClientMetrics.BoundaryError)
        .getCounter(ClientMetricsActions.Failure)
        .setContext({
          alertType: message === expectedReactCrashMessage ? 'expected' : 'unexpected',
        })
        .increment()
    } catch (error_) {
      console.error('Error logging boundary error', error_)
    }
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback
      }

      if (!this.isRouting) {
        // Suspend handling any subsequent errors until the route change is complete
        this.isRouting = true
        window.location.href = '/500?previousPath=' + encodeURIComponent(window.location.pathname)
      }
    }

    return this.props.children
  }
}

export default withRouter<ErrorBoundaryProps & WithRouterProps>(ErrorBoundary)
